import { forwardRef, useRef, type InputHTMLAttributes, type Ref } from "react";
import { Button, type ButtonProps } from "./Button";

export type FileInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

/** Native file selection for custom upload flows; FileDropzone owns drag/drop. */
export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  function FileInput({ hidden = true, ...props }, ref) {
    return <input {...props} ref={ref} type="file" hidden={hidden} />;
  },
);

export interface FileButtonProps extends Omit<ButtonProps, "onClick" | "onChange"> {
  accept?: string;
  multiple?: boolean;
  capture?: FileInputProps["capture"];
  onChange?: FileInputProps["onChange"];
  inputLabel?: string;
  inputRef?: Ref<HTMLInputElement>;
}

/** Satin upload action with native file selection, including same-file retries. */
export const FileButton = forwardRef<HTMLInputElement, FileButtonProps>(
  function FileButton({ accept, multiple, capture, onChange, inputLabel, inputRef, disabled, loading, children, ...props }, ref) {
    const localRef = useRef<HTMLInputElement>(null);
    function assignRef(node: HTMLInputElement | null) {
      localRef.current = node;
      for (const target of [ref, inputRef]) {
        if (typeof target === "function") target(node);
        else if (target) target.current = node;
      }
    }
    return <>
      <FileInput ref={assignRef} accept={accept} multiple={multiple} capture={capture}
        aria-label={inputLabel} disabled={disabled || loading}
        onChange={(event) => { onChange?.(event); event.currentTarget.value = ""; }} />
      <Button {...props} type="button" disabled={disabled} loading={loading}
        onClick={() => localRef.current?.click()}>{children}</Button>
    </>;
  },
);
